import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {StyleSheet, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';

import Button from '../../components/ui-controls/Button';
import Dialog from '../../components/ui-controls/Dialog';
import DropDownPicker from '../../components/ui-controls/DropDownPicker';
import FormView from '../../components/ui-controls/form/FormView';
import Label from '../../components/ui-controls/form/Label';
import TextInput from '../../components/ui-controls/form/TextInput';
import ValidationMessage from '../../components/ui-controls/form/ValidationMessage';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps, TicketPrefill} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import apiService from '../../transport/apiService';
import {logger} from '../../utils/logger';

import {TicketFormData, ticketSchema} from './schema';

type SuccessDialogProps = {
  isVisible: boolean;
  onOk: () => void;
  submittedTicketNumber: string;
};
const SuccessDialog = ({isVisible, onOk, submittedTicketNumber}: SuccessDialogProps) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    onOk={onOk}
    okText="CLOSE"
    title="Your ticket was successfully submitted. Thank you.">
    <View>
      <Text style={styles.dialogText}>Your ticket number is #{submittedTicketNumber}</Text>
    </View>
  </Dialog>
);

type ErrorDialogProps = {
  isVisible: boolean;
  onOk: () => void;
};
const ErrorDialog = ({isVisible, onOk}: ErrorDialogProps) => (
  <Dialog
    cancelVisible={false}
    isVisible={isVisible}
    okText="CLOSE"
    onOk={onOk}
    title="Sorry, we couldn't submit your ticket at this moment. Please try again later."
  />
);

const HelpContactSubmitTicketScreen = ({
  navigation,
  route,
}: RouteNavigatorScreenProps<AppRoute.CONTACT_US_SUBMIT_TICKET>) => {
  // DIS-1339: optional prefill (e.g. from the staff-activation block screen) wins
  // over store-derived defaults. CONTACT_US_SUBMIT_TICKET is a singleton screen
  // (getId in TabNavigator), so navigating in with new params does NOT remount it
  // and react-hook-form defaultValues / the uncontrolled inputs would otherwise
  // keep their mount-time values. Key the form on the prefill so re-entry with a
  // different prefill remounts the form and re-seeds every field.
  const prefill = route.params?.prefill;
  const formKey = prefill ? JSON.stringify(prefill) : 'no-prefill';

  return <TicketForm key={formKey} navigation={navigation} prefill={prefill} />;
};

type TicketFormProps = {
  navigation: RouteNavigatorScreenProps<AppRoute.CONTACT_US_SUBMIT_TICKET>['navigation'];
  prefill?: TicketPrefill;
};

const TicketForm = ({navigation, prefill}: TicketFormProps) => {
  const {shopSelectionStore, userStore} = useStores();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSuccessDialogVisible, setSuccessDialogVisible] = useState(false);
  const [isErrorDialogVisible, setErrorDialogVisible] = useState(false);
  const [submittedTicketNumber, setSubmittedTicketNumber] = useState('');

  // https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/rules#avoid-hard-coded-values 🤷🏻‍♂️
  const [enquiryTypes] = useState([
    {label: 'I am shopping in store', value: 'InStore'},
    {label: 'I am shopping online', value: 'Online'},
    {label: 'I am a Store Owner', value: 'LMG'},
    {label: 'I am a Staff Member', value: 'StaffMember'},
  ]);

  const defaultValues: TicketFormData = {
    firstName: prefill?.firstName ?? userStore.firstName ?? '',
    lastName: prefill?.lastName ?? userStore.lastName ?? '',
    email: prefill?.email ?? userStore.emailAddress ?? '',
    phoneNumber: prefill?.phoneNumber ?? userStore.phoneNumber ?? '',
    nameOfStore: prefill?.nameOfStore ?? shopSelectionStore.selectedStore?.name ?? '',
    enquiryType: prefill?.enquiryType ?? '',
    description: prefill?.description ?? '',
  };

  const {
    control,
    handleSubmit,
    formState: {errors, touchedFields},
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const handleSuccessDialogOk = () => {
    setSuccessDialogVisible(false);
    navigation.navigate(AppRoute.HOME);
  };

  const handleErrorDialogOk = () => {
    setErrorDialogVisible(false);
  };

  const onSubmit = async (values: TicketFormData) => {
    try {
      setSubmitting(true);
      const response = await apiService.createSupportTicket(values);
      const {ticketNumber} = response;
      if (!ticketNumber) {
        logger.error(
          'HelpContactSubmitTicketScreen',
          'Support Ticket was submitted successfully but no ticket number was received',
          {response},
        );
        throw new Error();
      }
      setSubmittedTicketNumber(ticketNumber);
      setSuccessDialogVisible(true);
    } catch (_err) {
      setErrorDialogVisible(true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderValidationMessage = (fieldName: keyof TicketFormData) => {
    const error = errors[fieldName];
    const touched = touchedFields[fieldName];

    return (
      <>
        {(!error || !touched) && <ValidationMessage> </ValidationMessage>}
        {!!(error && touched) && <ValidationMessage>{error.message}</ValidationMessage>}
      </>
    );
  };

  // The Android manifest is adjustPan, which never resizes the window, so the
  // keyboard inset has to be applied by the scroll view itself. DIS-1551.
  return (
    <KeyboardAwareScrollView
      style={{backgroundColor: 'white'}}
      keyboardShouldPersistTaps="always"
      enableOnAndroid
      extraScrollHeight={40}>
      <ContentView testID="submit-ticket-screen">
        <FormView>
          <View>
            <View>
              <Label>First name</Label>
              <Controller
                control={control}
                name="firstName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-given"
                    defaultValue={value}
                    error={errors.firstName?.message}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="First name"
                    touched={touchedFields.firstName}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('firstName')}
            </View>
            <View>
              <Label>Last name</Label>
              <Controller
                control={control}
                name="lastName"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    autoComplete="name-family"
                    defaultValue={value}
                    error={errors.lastName?.message}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Last name"
                    touched={touchedFields.lastName}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('lastName')}
            </View>
            <View>
              <Label>Email</Label>
              <Controller
                control={control}
                name="email"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="email"
                    defaultValue={value}
                    error={errors.email?.message}
                    keyboardType="email-address"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Email"
                    touched={touchedFields.email}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('email')}
            </View>
            <View>
              <Label>Phone Number</Label>
              <Controller
                control={control}
                name="phoneNumber"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="none"
                    autoComplete="tel"
                    defaultValue={value}
                    error={errors.phoneNumber?.message}
                    keyboardType="phone-pad"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Phone Number"
                    touched={touchedFields.phoneNumber}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('phoneNumber')}
            </View>
            <View>
              <Label>Enquiry Type</Label>
              <Controller
                control={control}
                name="enquiryType"
                render={({field: {onChange}}) => (
                  <DropDownPicker
                    items={enquiryTypes}
                    placeholder="Please select"
                    defaultValue={prefill?.enquiryType}
                    onSelectItem={enquiryType => onChange(enquiryType.value)}
                  />
                )}
              />
              {renderValidationMessage('enquiryType')}
            </View>
            <View>
              <Label>Name of store</Label>
              <Controller
                control={control}
                name="nameOfStore"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    autoCapitalize="words"
                    defaultValue={value}
                    error={errors.nameOfStore?.message}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    placeholder="Name of store"
                    style={styles.textInput}
                    touched={touchedFields.nameOfStore}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('nameOfStore')}
            </View>
            <View>
              <Label>How can we help you?</Label>
              <Controller
                control={control}
                name="description"
                render={({field: {onChange, onBlur, value}}) => (
                  <TextInput
                    numberOfLines={4}
                    autoComplete="off"
                    defaultValue={value}
                    error={errors.description?.message}
                    multiline={true}
                    onBlur={onBlur}
                    onChangeText={onChange}
                    touched={touchedFields.description}
                    renderErrorMessage={false}
                  />
                )}
              />
              {renderValidationMessage('description')}
            </View>
          </View>
          <Button
            onPress={handleSubmit(onSubmit)}
            title="Submit ticket"
            disabled={isSubmitting}
            loading={isSubmitting}
          />
          <SuccessDialog
            isVisible={isSuccessDialogVisible}
            onOk={handleSuccessDialogOk}
            submittedTicketNumber={submittedTicketNumber}
          />
          <ErrorDialog isVisible={isErrorDialogVisible} onOk={handleErrorDialogOk} />
        </FormView>
      </ContentView>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  dialogText: {
    marginTop: 8,
    marginBottom: 8,
    fontSize: 16,
  },
  textInput: {},
});

export default HelpContactSubmitTicketScreen;
